// Componente de UI de chat conversación 1-on-1. Reusable entre:
//   - DetallePaciente tab Mensajes (kine viendo conversación con paciente)
//   - MensajesPaciente (paciente viendo conversación con su kine)
//
// Sub-decisión: bubbles alineadas según `viewerRole`:
//   - viewerRole='kine'      → mensajes del kine van a la derecha (primary bg)
//   - viewerRole='paciente'  → mensajes del paciente van a la derecha
// El "yo" siempre derecha + primary, el "otro" izquierda + card neutral.
// Pattern WhatsApp/iMessage convencional.
//
// Auto-scroll al bottom cuando llegan mensajes nuevos. Marca como leídos
// al mount via prop callback. Send con Enter, Shift+Enter newline.

import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { Send, Loader2 } from "lucide-react";
import type {
  MensajeRow,
  MensajeSender,
} from "@/lib/supabase/queries/useMensajesConversacion";
import { formatRelativeDate } from "@/lib/utils";

interface Props {
  /** Mensajes ordenados ASC (oldest first). */
  mensajes: MensajeRow[];
  /** Rol del usuario viendo la conversación — define alineación de bubbles. */
  viewerRole: MensajeSender;
  /** Loading state inicial (sin data todavía). */
  loading: boolean;
  /** Error de fetch — mensaje string para mostrar arriba. */
  fetchError?: string | null;
  /** Disparado al click Send. Devuelve true si OK, false si falló. */
  onSend: (content: string) => Promise<boolean>;
  /** Loading state del send. */
  sending: boolean;
  /** Error del último send fallido — mensaje string. null si OK. */
  sendError?: string | null;
  /** Disparado al mount + cuando llegan mensajes nuevos no leídos del otro. */
  onMarkRead?: () => void;
}

export function ChatConversation({
  mensajes,
  viewerRole,
  loading,
  fetchError,
  onSend,
  sending,
  sendError,
  onMarkRead,
}: Props) {
  const [draft, setDraft] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);

  // Auto-scroll al final cuando cambia la lista o terminó de cargar.
  useEffect(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [mensajes.length, loading]);

  // Mark as read al mount y cuando llegan nuevos mensajes del otro sender
  // sin leer. Idempotente — la RPC chequea read_at IS NULL internamente.
  useEffect(() => {
    if (!onMarkRead) return;
    const hayUnreadDelOtro = mensajes.some(
      (m) => m.sender !== viewerRole && m.read_at === null,
    );
    if (hayUnreadDelOtro) {
      onMarkRead();
    }
  }, [mensajes, viewerRole, onMarkRead]);

  const handleSubmit = async () => {
    const trimmed = draft.trim();
    if (trimmed.length === 0) return;
    const ok = await onSend(trimmed);
    if (ok) setDraft("");
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void handleSubmit();
    }
  };

  return (
    <div className="flex flex-col h-full min-h-[400px] bg-card rounded-2xl border border-border overflow-hidden">
      {/* Mensajes scrollables */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto p-4 space-y-3"
        style={{ maxHeight: "calc(100vh - 280px)" }}
      >
        {loading && mensajes.length === 0 && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        )}

        {fetchError && !loading && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-900">
            No pudimos cargar los mensajes: {fetchError}
          </div>
        )}

        {!loading && mensajes.length === 0 && !fetchError && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <p className="text-sm text-muted-foreground">
              Todavía no hay mensajes en esta conversación.
            </p>
            <p className="text-xs text-muted-foreground/70 mt-1">
              Escribí abajo para empezar.
            </p>
          </div>
        )}

        {mensajes.map((m) => (
          <MessageBubble
            key={m.id}
            mensaje={m}
            isMine={m.sender === viewerRole}
          />
        ))}
      </div>

      {/* Input */}
      <div className="p-3 border-t border-border bg-card">
        {sendError && (
          <div className="mb-2 px-3 py-2 bg-red-50 border border-red-200 rounded-md text-xs text-red-900">
            {sendError}
          </div>
        )}
        <div className="flex gap-2 items-end">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Escribí un mensaje…"
            disabled={sending}
            rows={1}
            maxLength={2000}
            className="flex-1 px-4 py-2.5 text-sm bg-input-background border border-border rounded-2xl transition-all duration-200 ease-out focus:ring-2 focus:ring-ring focus:border-transparent resize-none max-h-32 disabled:opacity-50"
          />
          <button
            type="button"
            onClick={handleSubmit}
            disabled={sending || draft.trim().length === 0}
            aria-label="Enviar"
            className="w-11 h-11 shrink-0 bg-primary text-primary-foreground rounded-full flex items-center justify-center shadow-sm transition-all duration-200 ease-out hover:scale-[1.05] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
          >
            {sending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" strokeWidth={1.75} />
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

function MessageBubble({
  mensaje,
  isMine,
}: {
  mensaje: MensajeRow;
  isMine: boolean;
}) {
  const time = formatRelativeDate(new Date(mensaje.created_at));
  return (
    <div className={`flex ${isMine ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[80%] md:max-w-[70%] rounded-2xl px-4 py-2.5 ${
          isMine
            ? "bg-primary text-primary-foreground rounded-br-sm"
            : "bg-muted/50 text-foreground border border-border rounded-bl-sm"
        }`}
      >
        <p className="text-sm whitespace-pre-wrap break-words">
          {mensaje.content}
        </p>
        <p
          className={`text-[10px] mt-1 ${
            isMine
              ? "text-primary-foreground/70"
              : "text-muted-foreground"
          }`}
          title={new Date(mensaje.created_at).toLocaleString("es-AR")}
        >
          {time}
        </p>
      </div>
    </div>
  );
}
